import React from 'react';
import TableCellButton from '../reusable/AgGridCells/TableCellButton/TableCellButton.js';
import { FILTER_TYPES } from '../reusable/Filters/filterHelpers.js';
import { messageManager } from '../System/messageManager/messageManager.js';
import { HttpStatusCode } from 'axios';
import { ToastComponent } from '@tylertech/forge';
import { approveGroups, approveRequests, rejectGroups, rejectRequests, unDecideGroups, unDecideRequests } from '../../services/budgeterService.js';
import TruncatedCellRenderer, { tooltipValueGetter } from '../reusable/AgGridCells/TruncatedCellRenderer/TruncatedCellRenderer.js';
import { BUDGETER_PHASES_ID, TABLE_COLUMN_WIDTH, TABLE_LOCATIONS, USER_ROLE } from '../../static/constants.js';
import { ForgeTooltip } from '@tylertech/forge-react';
import { REQUEST } from '../../static/keyWords.js';
import { AGGridColumnNumberComparator } from '../System/AgGrid/RegisterAgGridModules.js';

export const INITIAL_SUCCESS_DIALOG_STATE = { open: false, requestId: null };
export const getBudgetRequestsHeaderBadges = (phase) => {
  return [
    {
      id: 'phase',
      theme: 'info-secondary',
      text: `Phase: ${phase}`,
    },
  ];
};

export const getBudgetRequestsHeaderButtons = (
  setOpenFilters,
  setOpenGraphs,
  openGraphs,
  setOpenLineItemDialog,
  setRequestType,
  handleExport,
  isExportButtonDisabled,
  setShowActivateBudget,
  user,
  nameConventions
) => {
  return [
    {
      id: 'more',
      name: 'More',
      variant: 'text',
      type: 'dropDown',
      popOverIcon: false,
      icon: 'dots_vertical',
      children: getMoreMenuOptions(isExportButtonDisabled, user),
      onClickFunction: (e) => {
        if (e.detail.value === 'export') {
          handleExport();
        } else {
          setShowActivateBudget(true);
        }
      },
    },
    {
      id: 'filter',
      name: 'Filter',
      variant: 'text',
      icon: 'filter_list',
      onClickFunction: () => setOpenFilters(true),
      filterBadge: true
    },
    {
      id: 'create',
      name: 'Create',
      variant: 'raised',
      type: 'dropDown',
      popOverIcon: true,
      before: true,
      children: CREATE_MENU_OPTIONS,
      style: { margin: '0px 0px 0px var(--forge-spacing-xsmall)' },
      onClickFunction: (e) => {
        setOpenLineItemDialog(true);
        setRequestType(e.detail.value);
      },
      disabled:
        user?.isViewOnly ||
        (user?.role === USER_ROLE.DEPARTMENT_USER && user?.activeBudget.budgeterPhase === BUDGETER_PHASES_ID.REVIEW_PHASE),
      tooltip: `${nameConventions[REQUEST].pluralFormAlias} cannot be created in review phase`
    },
    {
      id: 'requestSummary',
      name: 'Request summary',
      type: 'iconButton',
      icon: openGraphs ? 'hide_panel_right' : 'hide_panel_left',
      before: true,
      tooltip: !openGraphs ? 'Show graphic' : 'Close graphic',
      style: { margin: '0px 0px 0px 10px' },
      onClickFunction: () => {
        setOpenGraphs(!openGraphs);
      },
    },
  ];
};

const getMoreMenuOptions = (isExportButtonDisabled, user) => {
  return [
    {
      label: 'Export',
      value: 'export',
      icon: 'export',
      leadingIconType: 'component',
      leadingIconComponentProps: { external: true, externalType: 'all' },
      disabled: isExportButtonDisabled,
    },
    ...(user?.role !== USER_ROLE.DEPARTMENT_USER
      ? [
        {
          label: 'Activate budget',
          value: 'activateBudget',
          icon: 'account_reactivate',
          leadingIconType: 'component',
          leadingIconComponentProps: { external: true, externalType: 'all' },
          disabled: user?.isViewOnly,
        },
      ]
      : []),
  ];
};

export const REQUEST_TYPES = {
  INCREASE_ITEM_COST: 'Increase item cost',
  DECREASE_ITEM_COST: 'Decrease item cost',
  CREATE_NEW_ITEM_FROM_EXISTING: 'New item',
};

export const REQUEST_TYPES_DB = {
  INCREASE_ITEM_COST: 'increase',
  DECREASE_ITEM_COST: 'decrease',
  CREATE_NEW_ITEM_FROM_EXISTING: 'add-existing',
};

const CREATE_MENU_OPTIONS = [
  {
    label: REQUEST_TYPES.INCREASE_ITEM_COST,
    value: REQUEST_TYPES_DB.INCREASE_ITEM_COST
  },
  {
    label: REQUEST_TYPES.DECREASE_ITEM_COST,
    value: REQUEST_TYPES_DB.DECREASE_ITEM_COST
  },
  {
    label: REQUEST_TYPES.CREATE_NEW_ITEM_FROM_EXISTING,
    value: REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING
  },
];

export const INITIAL_SORT_ORDER = {
  orderBy: '',
  order: '',
};

export const SUMMARY_OF_REQUESTS_PAGINATION_DATA = {
  pageRequestCount: 5,
  pageNumber: 0,
};

export const LINE_ITEM_TABLE_PAGINATION_DATA = {
  pageItemCount: 10,
  pageNumber: 0,
};

export const DURATION_TYPE_ID = {
  ONGOING: 2,
  ONETIME: 1,
};

export const DURATION_TYPE_LABELS = {
  ONGOING: 'Ongoing',
  ONETIME: 'One time',
};

export const CAPITALIZED_REQUEST_STATUS = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const getStatusBadgeTheme = (status) => {
  return {
    Approved: 'success',
    Pending: 'info',
    Rejected: 'error',
  }[status];
};

// not being used
export const adaptItemData = (itemData) => {
  const adaptedItemData = itemData.map((item) => {
    return createData(
      item.status,
      item.accountCode,
      item.categoryLevelOne,
      item.type,
      item.requestDescription,
      item.duration === 'Ongoing'
        ? DURATION_TYPE_LABELS.ONGOING
        : DURATION_TYPE_LABELS.ONETIME,
      item.requestedAmount,
      item.id,
      item.costCenterName,
      item.isOffset,
      item.ongoingAmount,
      item.onetimeAmount,
      item.approvedAmount
    );
  });
  return adaptedItemData;
};

// not being used
const createData = (
  status,
  accountCode,
  categoryLevelOne,
  type,
  requestDescription,
  duration,
  requestedAmount,
  id,
  costCenterName,
  isOffset,
  ongoingAmount,
  onetimeAmount
) => {
  return {
    status,
    accountCode,
    categoryLevelOne,
    type,
    requestDescription,
    duration,
    requestedAmount,
    id,
    costCenterName,
    isOffset,
    ongoingAmount,
    onetimeAmount,
  };
};

export const getSelectionToolBarButtons = (ungroup, approve, reject, convertToPending, user, selectedRows) => {
  return [
    {
      id: 'ungroup',
      type: 'selection',
      name: 'Ungroup',
      icon: 'ungroup',
      onClickFunction: () => ungroup(),
      disabled: user?.isViewOnly,
      showCondition: true
    },
    {
      id: 'approve',
      type: 'selection',
      name: 'Approve',
      icon: 'check_circle_outline',
      onClickFunction: () => approve(),
      disabled: user?.isViewOnly,
      showCondition: decideShow(selectedRows, REQUEST_STATUS.APPROVED),
    },
    {
      id: 'reject',
      type: 'selection',
      name: 'Reject',
      icon: 'close_circle_outline',
      onClickFunction: () => reject(),
      disabled: user?.isViewOnly,
      showCondition: decideShow(selectedRows, REQUEST_STATUS.REJECTED),
    },
    {
      id: 'undecide',
      type: 'selection',
      name: 'Convert to pending',
      icon: 'undo_variant',
      onClickFunction: () => convertToPending(),
      disabled: user?.isViewOnly,
      showCondition: decideShow(selectedRows, REQUEST_STATUS.PENDING),
    },
  ];
};

const decideShow = (selectedRows, status) => {
  switch (status) {
    case REQUEST_STATUS.APPROVED:
      return selectedRows.some((row) => row.status.toLowerCase() !== REQUEST_STATUS.APPROVED);
    case REQUEST_STATUS.REJECTED:
      return selectedRows.some((row) => row.status.toLowerCase() !== REQUEST_STATUS.REJECTED);
    case REQUEST_STATUS.PENDING:
      return selectedRows.some((row) => row.status.toLowerCase() !== REQUEST_STATUS.PENDING);
    default:
      return false;
  }
};

export const formatCurrency = (value) => {
  if (value === '' || value === undefined) return value;
  if (value === null) value = 0;
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

export const getBudgetRequestsFilters = () => {
  return [
    FILTER_TYPES.OFFSET,
    FILTER_TYPES.DURATION_TYPE,
    FILTER_TYPES.COST_TYPE,
    FILTER_TYPES.ACCOUNT_TYPE,
    FILTER_TYPES.FUND,
    FILTER_TYPES.BUDGETER_ORGANIZATION_LEVELS,
    FILTER_TYPES.BUDGETER_PERSONNEL,
    FILTER_TYPES.BUDGETER_NON_PERSONNEL,
    FILTER_TYPES.REQUEST_STATUS,
    FILTER_TYPES.REQUEST_TYPE,
  ];
};

export const getOrgLevelsNames = (filtersSettings) =>
  filtersSettings
    ?.find(f => f.type === FILTER_TYPES.BUDGETER_ORGANIZATION_LEVELS)
    ?.options
    ?.map(option => option.name);

const buildDynamicCols = (orgLevelNames = [], showColumns) =>
  (orgLevelNames || []).map((orgLevel, index) => ({
    colId: `organizationLevelsMap.${index}`,
    headerName: orgLevel,
    headerTooltip: orgLevel,
    field: `organizationLevelsMap.${orgLevel}`,
    tooltipField: `organizationLevelsMap.${orgLevel}`,
    sortable: true,
    context:'Level_'+ (index+1),
    hide: !showColumns?.find((column) => column.name === 'Level_'+ (index+1))?.show,
    width: TABLE_COLUMN_WIDTH.XL,
  }));

export const getLineItemTableColumns = (
  handleActionButtonClick,
  orgLevelNames = [],
  showColumns,
  nameConventions
) => {
  let showColumnsItemRequest = showColumns ? showColumns[TABLE_LOCATIONS.lineItemRequest] : []; 

  const dynamicOrgLevelCols = buildDynamicCols(orgLevelNames, showColumnsItemRequest);

  return [
    {
      colId: 'accountCode',
      headerName: 'AcctCode',
      headerTooltip: 'AcctCode',
      field: 'accountCode',
      tooltipField: 'accountCode',
      sortable: true,
      context: 'Account code',
      hide: !showColumnsItemRequest?.find((column) => column.name === 'Account code')?.show,
      width: TABLE_COLUMN_WIDTH.XL,
    },
    ...dynamicOrgLevelCols,
    {
      colId: 'categoryLevelOne',
      headerName: 'Item',
      headerTooltip: 'Item',
      field: 'categoryLevelOne',
      tooltipField: 'categoryLevelOne',
      sortable: true,
      context: 'Item',
      hide: !showColumnsItemRequest?.find((column) => column.name === 'Item')?.show,
      width: TABLE_COLUMN_WIDTH.DEFAULT
    },
    {
      colId: 'costType',
      headerName: 'Cost type',
      headerTooltip: 'Cost type',
      field: 'objectType',
      tooltipField: 'objectType',
      sortable: true,
      context: 'Cost type',
      hide: !showColumnsItemRequest?.find((column) => column.name === 'Cost type')?.show,
      width: TABLE_COLUMN_WIDTH.MD,
    },
    {
      colId: 'totalCost',
      headerName: 'Total item cost',
      headerTooltip: 'Total item cost',
      field: 'totalCost',
      tooltipValueGetter: tooltipValueGetter,
      valueFormatter: (params) => {
        return formatCurrency(params.value);
      },
      cellRenderer: TruncatedCellRenderer,
      cellRendererParams: (params) => ({
        main: params.value.toString(),
        number: 0,
        value: formatCurrency(params.value),
        type: 'textWithIcon',
      }),
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      sortable: true,
      context: 'Total item cost',
      hide: !showColumnsItemRequest?.find((column) => column.name === 'Total item cost')?.show,
      comparator: AGGridColumnNumberComparator,
      width: TABLE_COLUMN_WIDTH.LG,
    },
    {
      colId: 'durationType',
      headerName: 'Duration type',
      headerTooltip: 'Duration type',
      field: 'durationType',
      tooltipField: 'durationType',
      sortable: true,
      context: 'Duration type',
      hide: !showColumnsItemRequest?.find((column) => column.name === 'Duration type')?.show,
      width: TABLE_COLUMN_WIDTH.MD
    },
    {
      colId: 'action',
      headerName: 'Action',
      headerTooltip: 'Action',
      field: '',
      filter: false,
      suppressFloatingFilterButton: true,
      cellRenderer: (params) => {
        return (
          <>
            <TableCellButton
              id={'budget-create-request-button'}
              label={'Item info'}
              data={params.data}
              icon={'chevron_right'}
              handleClick={() => handleActionButtonClick(params.data.id)}
            />
            <ForgeTooltip anchor="budget-create-request-button" placement={'bottom'}>
              {`Create ${nameConventions[REQUEST].alias}`}
            </ForgeTooltip>
          </>
        );
      },
      cellClass: 'grid-cell-centered',
      flex: 0.5,
      sortable: false,
      pinned: 'right',
      context: 'Action',
      hide: false,
      suppressColumnsToolPanel: true,
      width: TABLE_COLUMN_WIDTH.XS,
    }
  ];
};

export const getAccountTypeId = (value) => (value === 0 ? 2 : 4);

const REQUEST_ACTIONS_APPROVE = 'approved';
const REQUEST_ACTIONS_REJECT = 'rejected';
const REQUEST_ACTIONS_CONVERT_TO_PENDING = 'converted to pending';

export const handleApproveRequests = (requestsToApprove, onFinish, setActionFailedDialog, nameConventions, setLoading = () => {}) => {
  const messages = messageManager(nameConventions);
  approveRequests(requestsToApprove)
    .then((result) => {
      if (result.status === HttpStatusCode.Ok) {
        manageRequestActionPopups(REQUEST_ACTIONS_APPROVE, requestsToApprove, result.data.failedRequests, result.data.duplicatedRequests, setActionFailedDialog, nameConventions);
        onFinish && onFinish();
      }
    })
    .catch(() => {
      ToastComponent.present({
        message: messages.BUDGET_REQUESTS.APPROVE_REQUEST_GENERIC_ERROR,
      });
    }).finally(() => { setLoading(false); });
};

export const handleConvertRequestsToPending = (requestsToConvert, onFinish, setActionFailedDialog, nameConventions, setLoading = () => {}) => {
  const messages = messageManager(nameConventions);
  unDecideRequests(requestsToConvert)
    .then((result) => {
      if (result.status === HttpStatusCode.Ok) {
        manageRequestActionPopups(REQUEST_ACTIONS_CONVERT_TO_PENDING, requestsToConvert, result.data.failedRequests, result.data.duplicatedRequests, setActionFailedDialog, nameConventions);
        onFinish && onFinish();
      }
    })
    .catch(() => {
      ToastComponent.present({
        message: messages.BUDGET_REQUESTS.APPROVE_REQUEST_GENERIC_ERROR,
      });
    }).finally(() => { setLoading(false); });
};

export const handleRejectRequests = (requestsToReject, onFinish, setActionFailedDialog, nameConventions, setLoading = () => {}) => {
  const messages = messageManager(nameConventions);
  rejectRequests(requestsToReject)
    .then((result) => {
      if (result.status === HttpStatusCode.Ok) {
        manageRequestActionPopups(REQUEST_ACTIONS_REJECT, requestsToReject, result.data.failedRequests, result.data.duplicatedRequests, setActionFailedDialog, nameConventions);
        onFinish && onFinish();
      }
    })
    .catch(() => {
      ToastComponent.present({
        message: messages.BUDGET_REQUESTS.APPROVE_REQUEST_GENERIC_ERROR,
      });
    }).finally(() => { setLoading(false); });
};

const manageRequestActionPopups = (action, requests, failedRequests, duplicatedRequests, setActionFailedDialog, nameConventions) => {
  const messages = messageManager(nameConventions);

  if (duplicatedRequests.length > 0) {
    setActionFailedDialog({ open: true, requestId: duplicatedRequests[0], message: messages.BUDGET_REQUESTS.REQUEST_ACTION_DUPLICATED_ITEMS_ERROR });
    return;
  }

  if (failedRequests.length > 0) {
    setActionFailedDialog({ open: true, requestId: failedRequests[0], message: messages.BUDGET_REQUESTS.REQUEST_ACTION_ALLOCATION_ISSUE(action, requests.length, failedRequests.length) });
    return;
  }

  ToastComponent.present({
    message: messages.BUDGET_REQUESTS.REQUEST_ACTION_SUCCESS_MESSAGE(action, requests.length > 1),
  });
};

export const handleApproveGroups = (groupIds, onFinish, setLoading, setGroupActionFailedDialog, nameConventions) => {
  setLoading && setLoading(true);

  approveGroups(groupIds)
    .then((result) => {
      if (result.status === HttpStatusCode.Ok) {
        manageGroupRequestActionPopups(
          REQUEST_ACTIONS_APPROVE,
          groupIds,
          result.data.failedGroups,
          setGroupActionFailedDialog,
          nameConventions
        );
        onFinish && onFinish();
      }
    })
    .catch(() => {
      ToastComponent.present({
        message: messageManager(nameConventions).BUDGET_REQUESTS.GROUP_APPROVED_ERROR,
      });
    }).finally(() => { setLoading && setLoading(false); });
};

export const handleUndecideGroups = (groupIds, onFinish, setLoading, setGroupActionFailedDialog, nameConventions) => {
  setLoading && setLoading(true);

  unDecideGroups(groupIds)
    .then((result) => {
      if (result.status === HttpStatusCode.Ok) {
        manageGroupRequestActionPopups(
          REQUEST_ACTIONS_CONVERT_TO_PENDING,
          groupIds,
          result.data.failedGroups,
          setGroupActionFailedDialog,
          nameConventions
        );
        onFinish && onFinish();
      }
    })
    .catch(() => {
      ToastComponent.present({
        message: messageManager(nameConventions).BUDGET_REQUESTS.GROUP_APPROVED_ERROR,
      });
    }).finally(() => { setLoading && setLoading(false); });
};


export const handleRejectGroups = (groupIds, onFinish, setLoading, setGroupActionFailedDialog, nameConventions) => {
  setLoading && setLoading(true);

  rejectGroups(groupIds)
    .then((result) => {
      if (result.status === HttpStatusCode.Ok) {
        manageGroupRequestActionPopups(
          REQUEST_ACTIONS_REJECT,
          groupIds,
          result.data.failedGroups,
          setGroupActionFailedDialog,
          nameConventions
        );
        onFinish && onFinish();
      }
    })
    .catch(() => {
      ToastComponent.present({
        message: messageManager(nameConventions).BUDGET_REQUESTS.GROUP_REJECTED_ERROR,
      });
    }).finally(() => { setLoading && setLoading(false); });
};

const manageGroupRequestActionPopups = (action, groupIds, failedGroups, setGroupActionFailedDialog, nameConventions) => {
  const messages = messageManager(nameConventions);

  if (failedGroups.length > 0) {
    setGroupActionFailedDialog({ open: true, groupId: failedGroups[0], message: messages.BUDGET_REQUESTS.REQUEST_GROUPS_ACTION_ALLOCATION_ISSUE(action, groupIds.length, failedGroups.length) });
    return;
  }

  ToastComponent.present({
    message: messages.BUDGET_REQUESTS.GROUP_ACTION_SUCCESS_MESSAGE(action, groupIds.length > 1),
  });
};

export const parseAgGridFilterModelToSearchKeyword = (filterModel) => {
  const _searchKeywords = [];
  for (const [key, value] of Object.entries(filterModel)) {
    const conditions = value.conditions ? 
      value.conditions.map(c => {
        return {
          type: c.type,
          value: c.filter
        };
      }) : [
        {
          type: value.type,
          value: value.filter
        }
      ];

    _searchKeywords.push({
      field: key,
      conditions: conditions,
      operator: value.conditions ? value.operator : 'AND'
    });
  }

  return _searchKeywords;
};
